/**
 * HeadWithdrawHistory 헬퍼 함수
 * 본사 입출금현황 관련 유틸리티
 */

import type { CSSProperties } from 'react';
import { HeadWithdrawHistory } from '../types/headWithdraw.types';

// ======== 타입 정의 ========

/** 필터 상태 타입 */
export interface WithdrawHistoryFilters {
    startDate: string;
    endDate: string;
    supplierId?: string;
    supplierName?: string;
    riderId?: string;
    riderName?: string;
}

// ======== 상수 ========

/** 테이블 컬럼 수 (빈 데이터 표시용) */
export const TABLE_COLUMN_COUNT = 13;

/** 거래 유형별 라벨 */
const TRANSACTION_LABELS: Record<string, string> = {
    deposit: '입금',
    withdrawal: '출금'
};

/** 적용 타입별 라벨 */
const APPLY_TYPE_LABELS: Record<string, string> = {
    RP: '라이더선정산',
    RW: '라이더주정산',
    SW: '협력사주정산',
    RR: '라이더재등록',
    SR: '협력사재등록',
    D: '입금',
    W: '출금',
    C: '입금취소'
};

/** 거래 유형별 뱃지 클래스 */
const BADGE_CLASSES: Record<string, string> = {
    point: 'badge-success',
    withdrawal: 'badge-danger'
};

// ======== 스타일 상수 ========

/** 금액 셀 스타일 생성 */
export const getAmountCellStyle = (isDeposit: boolean): CSSProperties => ({
    textAlign: 'right',
    color: isDeposit ? 'blue' : 'red',
    whiteSpace: 'nowrap',
    paddingRight: '1rem',
    fontSize: '1.1rem',
});

/** 페이지네이션 컨테이너 스타일 */
export const PAGINATION_CONTAINER_STYLE: CSSProperties = {
    marginTop: '1.5rem',
    display: 'flex',
    justifyContent: 'center'
};

/** 빈 데이터 셀 스타일 */
export const EMPTY_CELL_STYLE: CSSProperties = {
    textAlign: 'center',
    padding: '2rem'
};

// ======== 헬퍼 함수 ========

/**
 * 거래 유형 라벨 반환
 * @param history 입출금 내역
 */
export const getTransactionLabel = (history: HeadWithdrawHistory): string => {
    // SDC/HDC 일차감 코드 우선 처리 (acctType 또는 acctTypeName 기준)
    if (history.acctType === 'SDC' || history.acctTypeName === 'SDC') return '협력사 일차감';
    if (history.acctType === 'HDC' || history.acctTypeName === 'HDC') return '본사 일차감';

    // 백엔드에서 매핑된 acctTypeName이 있으면 우선 사용
    if (history.acctTypeName && history.acctTypeName.trim() !== '') {
        return history.acctTypeName;
    }

    // 입금취소 확인
    if (history.acctType === 'C') {
        return '입금취소';
    }

    // 송금 타입들에 대한 기본값 (백엔드와 동일)
    if (history.acctType === 'TO') {
        return '송금(출금)';
    }
    if (history.acctType === 'TI') {
        return '송금(입금)';
    }
    if (history.acctType === 'T') {
        return '본사입금';
    }

    // 수수료 타입
    if (history.acctType === 'F') {
        return '수수료';
    }

    // 정산 타입들
    if (history.acctType === 'SW') {
        return '협력사주정산';
    }
    if (history.acctType === 'RW') {
        return '라이더주정산';
    }
    if (history.acctType === 'RP') {
        return '라이더선정산';
    }

    // 재등록 타입들
    if (history.acctType === 'RR') {
        return '라이더재등록';
    }
    if (history.acctType === 'SR') {
        return '협력사재등록';
    }

    return TRANSACTION_LABELS[history.transactionType] || '입금';
};

/**
 * 적용 타입 라벨 반환
 * @param history 입출금 내역
 */
export const getApplyTypeLabel = (history: HeadWithdrawHistory): string => {
    return APPLY_TYPE_LABELS[history.acctType || ''] ||
        (history.transactionType === 'deposit' ? '입금' : '출금');
};

/**
 * 뱃지 클래스 반환
 * @param history 입출금 내역
 */
export const getBadgeClass = (history: HeadWithdrawHistory): string => {
    // 정산서 삭제 (SD, RD) - 주황색 테두리
    if (history.acctType === 'SD' || history.acctType === 'RD') {
        return 'badge-settlement-delete';
    }

    // 정산서 재등록 (SR, RR) - 녹색 테두리
    if (history.acctType === 'SR' || history.acctType === 'RR') {
        return 'badge-settlement-reregister';
    }

    // 금액이 음수면 빨간색 (출금/차감)
    if (history.amount < 0) {
        return 'badge-danger';
    }

    // 금액이 양수면 파란색 (입금/적립)
    if (history.amount > 0) {
        return 'badge-success';
    }

    // 0이면 회색
    return 'badge-secondary';
};

/**
 * 기본 필터 생성 (오늘 기준 7일 전 ~ 오늘)
 */
export const createDefaultFilters = (): WithdrawHistoryFilters => {
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7);

    return {
        startDate: oneWeekAgo.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
        supplierId: '',
        supplierName: '',
        riderId: '',
        riderName: ''
    };
};
